import React from 'react';

const CoreOfferingsSection = () => {
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a0a',
      padding: '5rem 2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem',
      maxWidth: '800px',
      margin: '0 auto 4rem auto'
    },
    title: {
      fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
      fontWeight: '700',
      color: 'white',
      marginBottom: '1.5rem',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
      color: 'rgba(255,255,255,0.7)',
      lineHeight: '1.6',
      fontWeight: '400'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    card: {
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '16px',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      flexDirection: 'column'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    icon: {
      width: '3rem',
      height: '3rem',
      borderRadius: '8px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: 'white',
      flexShrink: 0
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: 'white',
      margin: 0
    },
    cardDescription: {
      fontSize: '1rem',
      color: 'rgba(255,255,255,0.7)',
      lineHeight: '1.5',
      marginBottom: '1.5rem'
    },
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 2rem 0'
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '0.75rem',
      fontSize: '0.95rem',
      color: 'rgba(255,255,255,0.8)'
    },
    bullet: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#667eea',
      flexShrink: 0
    },
    learnMoreButton: {
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.2)',
      color: 'rgba(255,255,255,0.9)',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      width: '100%',
      marginTop: 'auto'
    },
    arrow: {
      fontSize: '1.1rem',
      transition: 'transform 0.3s ease'
    },
    hoverEffect: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      zIndex: -1
    }
  };

  const offerings = [
    {
      id: 'student-memberships',
      title: 'Student Memberships',
      icon: '📚',
      description: 'Access courses, resources, and global AI community.',
      features: ['Online courses', 'Community access', 'Progress tracking', 'Certificates']
    },
    {
      id: 'university-partnerships',
      title: 'University Partnerships',
      icon: '🏛️',
      description: 'Collaborative programs with leading institutions.',
      features: ['Curriculum development', 'Faculty training', 'Research collaboration', 'Accreditation']
    },
    {
      id: 'ai-workshops',
      title: 'AI Workshops',
      icon: '🔧',
      description: 'Hands-on training sessions and skill-building.',
      features: ['Practical sessions', 'Expert instructors', 'Interactive learning', 'Real projects']
    },
    {
      id: 'ai-publications',
      title: 'AI Publications',
      icon: '📄',
      description: 'Research papers, guides, and educational content.',
      features: ['Research papers', 'Study guides', 'Case studies', 'Industry reports']
    },
    {
      id: 'global-internships',
      title: 'Global Internships',
      icon: '🌐',
      description: 'International AI internship opportunities.',
      features: ['Global placements', 'Mentorship', 'Real experience', 'Career support']
    },
    {
      id: 'industry-visits',
      title: 'Industry Visits',
      icon: '🏢',
      description: 'Exclusive access to AI companies and labs.',
      features: ['Company tours', 'Expert talks', 'Networking', 'Career insights']
    }
  ];

  return (
    <div style={styles.container} data-component="container">
      <style>
        {`
          [data-component="card"]:hover {
            transform: translateY(-5px) !important;
            border-color: rgba(102, 126, 234, 0.3) !important;
          }
          [data-component="card"]:hover .hover-effect {
            opacity: 1 !important;
          }
          [data-component="learn-more-button"]:hover {
            background: rgba(102, 126, 234, 0.1) !important;
            border-color: rgba(102, 126, 234, 0.4) !important;
            color: white !important;
          }
          [data-component="learn-more-button"]:hover .arrow {
            transform: translateX(5px) !important;
          }
          @media (max-width: 800px) {
            [data-component="grid-container"] {
              grid-template-columns: 1fr !important;
              gap: 2.5rem !important;
            }
            [data-component="card"] {
              width: 90% !important;
              margin: 0 auto !important;
            }
          }
          @media (max-width: 480px) {
            [data-component="container"] {
              padding: 4rem 1rem !important;
            }
            [data-component="card"] {
              padding: 1.5rem !important;
            }
          }
        `}
      </style>
      
      <div style={styles.header}>
        <h1 style={styles.title}>Core Offerings</h1>
        <p style={styles.subtitle}>
          Comprehensive AI education programs designed for every learner, educator, and 
          institution worldwide.
        </p>
      </div>
      
      <div style={styles.gridContainer} data-component="grid-container">
        {offerings.map((offering) => (
          <div
            key={offering.id}
            style={styles.card}
            data-component="card"
          >
            <div className="hover-effect" style={styles.hoverEffect}></div>
            
            <div style={styles.cardHeader}>
              <div style={styles.icon}>
                {offering.icon}
              </div>
              <h3 style={styles.cardTitle}>{offering.title}</h3>
            </div>
            
            <p style={styles.cardDescription}>
              {offering.description}
            </p>
            
            <ul style={styles.featuresList}>
              {offering.features.map((feature, index) => (
                <li key={index} style={styles.featureItem}>
                  <div style={styles.bullet}></div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoreOfferingsSection;